import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquare, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { initials, relativeTime } from "@/lib/utils-app";
import { friendlyStatus } from "@/lib/citizen";

export default function Messages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/messages")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Group by case
  const grouped = items.reduce((acc, m) => {
    (acc[m.case_id] = acc[m.case_id] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Meddelanden</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        Mina meddelanden
      </h1>
      <p className="mt-2 text-zinc-500">All kommunikation samlad – per ärende.</p>

      {loading ? (
        <div className="flex justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="mt-8 bg-white border border-zinc-200 rounded-sm py-16 text-center">
          <MessageSquare className="w-10 h-10 text-zinc-300 mx-auto stroke-[1.5]" />
          <div className="mt-4 text-sm text-zinc-500">Inga meddelanden ännu.</div>
          <div className="text-xs text-zinc-400 mt-1">Du och din handläggare kommunicerar via ärendena.</div>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {Object.entries(grouped).map(([caseId, msgs]) => {
            const first = msgs[0];
            const s = friendlyStatus(first.case_status);
            return (
              <Link
                key={caseId}
                to={`/cases/${caseId}`}
                data-testid={`msg-thread-${caseId}`}
                className="block bg-white border border-zinc-200 rounded-sm overflow-hidden hover:border-blue-400 transition"
              >
                <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                      {first.case_ref}
                    </div>
                    <div className="text-sm font-medium text-zinc-900 truncate">{first.case_title}</div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium border rounded-sm ${s.classes}`}>
                    {s.short}
                  </span>
                </div>
                <div className="divide-y divide-zinc-100">
                  {msgs.slice(0, 3).map((m) => {
                    const mine = m.created_by === user?.user_id;
                    return (
                      <div key={m.comment_id} className="px-5 py-3 flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                          mine ? "bg-blue-700 text-white" : "bg-zinc-100 text-zinc-700"
                        }`}>
                          {initials(m.created_by_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-medium text-zinc-700">{m.created_by_name}</span>
                            {m.created_by_role === "admin" && (
                              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm">
                                Handläggare
                              </span>
                            )}
                            <span className="text-zinc-400">· {relativeTime(m.created_at)}</span>
                          </div>
                          <div className="text-sm text-zinc-700 mt-0.5 line-clamp-2">{m.text}</div>
                        </div>
                      </div>
                    );
                  })}
                  {msgs.length > 3 && (
                    <div className="px-5 py-2.5 text-xs text-blue-700 flex items-center gap-1">
                      +{msgs.length - 3} fler meddelanden <ArrowRight className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
